import React, { useState } from "react";
import { Link } from "gatsby";
import { Arrow, Pin, Book } from "@images/icons";
import { twMerge } from "tailwind-merge";

interface PrimaryButtonProps {
  externalText: string;
  internalText: string;
  internalPath?: string;
  externalUrl?: string;
  new_window?: "_blank" | "_self" | string;
  icon?: PrimaryButtonIcon;
  width?: number;
  className?: string;
}

// Controls the options for the PrimaryButton icon
export type PrimaryButtonIcon = "arrow" | "book" | "pin";

const PrimaryButton = ({
  width = 260,
  icon = "arrow",
  new_window = "_self",
  className,
  ...props
}: PrimaryButtonProps) => {
  // Initializing state for slide animation on hover
  const [isShown, setIsShown] = useState(false);

  // Assigning Icon based on passed prop
  let Icon;
  switch (icon) {
    case "pin":
      Icon = Pin;
      break;
    case "book":
      Icon = Book;
      break;
    default:
      Icon = Arrow;
  }

  /* Ensuring that an internal path is supplied (ensuring not an empty string or not undefined).
  If it is supplied, then render a Gatsby Link (<Link></Link>) otherwise render as a default anchor tag (<a></a>). */
  const Component =
    props.internalPath != "" && props.internalPath != undefined ? Link : "a";
  return (
    <Component
      to={props.internalPath as string}
      href={props.externalUrl}
      target={new_window}
      onMouseEnter={() => setIsShown(true)}
      onMouseLeave={() => setIsShown(false)}
      style={{ width: width }}
      className={twMerge(
        "relative flex h-[50px] cursor-pointer items-center justify-center overflow-hidden rounded-full bg-redWood font-heading text-2xl tracking-[1.44px] text-offWhite shadow-[2px_2px_4px_#5D5054CC]",
        className
      )}
    >
      <div
        className={`absolute z-[2] flex h-full w-full items-center justify-center bg-lightPink transition-all duration-150 ${
          isShown && "translate-x-full"
        }`}
      >
        <div
          className={`absolute left-0 z-[4] flex h-[50px] w-[50px] items-center justify-center rounded-full bg-salmon text-redWood ${
            isShown && "-translate-x-full"
          }`}
        >
          <Icon className="w-[33px] fill-redWood" />
        </div>
        <span className="ml-[10%] text-redWood">{props.externalText}</span>
      </div>
      <span className="-ml-[10%]">{props.internalText}</span>
    </Component>
  );
};

export default PrimaryButton;

// Types for data from Kontent.ai
export type KontentPrimaryButton = {
  system: { type: string };
  elements: {
    external_text: { value: string };
    internal_text: { value: string };
    internal_path: { value: string };
    external_url: { value: string };
    new_window: { value: [{ codename: string }] };
    icon: { value: [{ codename: PrimaryButtonIcon }] };
  };
};
